/**
 * Scene Writing Write draft cost band (#1193). Planning math only —
 * labeled as an estimate, never a bill and never a reason to refuse.
 *
 * Rates come from the OpenRouter catalog (same join as Create Outline
 * and Plot Grid Generate). Input size comes from the scene-writing
 * estimate route (`estimated_input_tokens`, or `prompt_chars` / 4).
 *
 * Write draft is the draft call, plus any later parts in `extraParts`
 * (a draft check can be added without rewriting this). With no extra
 * parts the band is still one call. The generation call still stops
 * at 8,192 tokens. The figure uses a shorter typical and worst, from
 * drafts that actually landed. Continue is a later click and is not
 * in this band.
 *
 * typical = I×Rp + 1400×Rc
 * worst   = I×Rp + 2800×Rc
 */

import {
  formatEstimateAbout,
  formatEstimateRange,
  formatEstimateUsd,
  pricingFromCatalog,
} from './estimateOutlineCost';

export const PROSE_SCENE_GENERATION_KEY = 'prose_scene_generation';

/** Backend DEFAULT_MODELS['prose_scene_generation'] when Settings has no override. */
export const PROSE_SCENE_GENERATION_DEFAULT_MODEL = 'anthropic/claude-sonnet-5';

export const CHARS_PER_TOKEN = 4;

/** Generation still stops at 8,192. The shown band follows real drafts. */
export const WRITE_DRAFT_OUTPUT_BUDGET = 8192;
export const WRITE_DRAFT_TYPICAL_OUTPUT = 1400;
export const WRITE_DRAFT_SHOWN_WORST = 2800;

export const CANT_ESTIMATE_RATES_LINE =
  "Can't estimate — OpenRouter rates didn't load. You can still confirm.";

export const CANT_ESTIMATE_SIZE_LINE =
  "Can't estimate — prompt size didn't load. You can still confirm.";

/** #1468: the figure did not arrive. Confirm still runs. */
export const CANT_ESTIMATE_NOW_LINE =
  "Can't estimate this run right now. You can still confirm.";

/** How long "Loading estimate…" may stay before the fallback line. */
export const ESTIMATE_SLOW_MS = 8000;

export const ESTIMATE_NOT_EXACT = 'Not an exact cost.';

export function resolveInputTokens({ estimatedInputTokens, promptChars } = {}) {
  if (estimatedInputTokens != null && estimatedInputTokens !== '') {
    const tokens = Number(estimatedInputTokens);
    if (Number.isFinite(tokens) && tokens >= 0) return Math.floor(tokens);
  }
  if (promptChars != null && promptChars !== '') {
    const chars = Number(promptChars);
    if (Number.isFinite(chars) && chars >= 0) return Math.floor(chars / CHARS_PER_TOKEN);
  }
  return null;
}

export function resolveProseModelId(modelPreferences) {
  const saved = modelPreferences?.[PROSE_SCENE_GENERATION_KEY];
  const text = typeof saved === 'string' ? saved.trim() : '';
  return text || PROSE_SCENE_GENERATION_DEFAULT_MODEL;
}

function priceScenePart(part) {
  const pricing = part?.pricing || part;
  const prompt = Number(pricing?.prompt);
  const completion = Number(pricing?.completion);
  const inputTokens = Number(part?.inputTokens);
  const typicalOutput = Number(part?.typicalOutput);
  const worstOutput = Number(part?.worstOutput);
  if (![prompt, completion, inputTokens, typicalOutput, worstOutput].every(Number.isFinite)) {
    return null;
  }
  if (inputTokens < 0 || typicalOutput < 0 || worstOutput < 0) return null;
  return {
    id: part.id || 'part',
    typical: inputTokens * prompt + typicalOutput * completion,
    worst: inputTokens * prompt + worstOutput * completion,
    inputTokens,
    typicalOutput,
    worstOutput,
  };
}

/**
 * The draft, then any extra parts a later build adds (a draft check).
 * Each extra part carries its own inputTokens and pricing.
 * @returns {object[]|null}
 */
export function sceneWritingCostParts(modelPricing, size = {}) {
  const inputTokens = resolveInputTokens(size);
  if (inputTokens == null) return null;
  const typicalOutput = Number(size.typicalOutput ?? WRITE_DRAFT_TYPICAL_OUTPUT);
  const worstOutput = Number(size.worstOutput ?? size.outputBudget ?? WRITE_DRAFT_SHOWN_WORST);
  const extras = Array.isArray(size.extraParts) ? size.extraParts : [];
  return [
    {
      id: 'draft',
      pricing: modelPricing,
      inputTokens,
      typicalOutput,
      worstOutput,
    },
    ...extras,
  ];
}

/**
 * @returns {{typical: number, worst: number, inputTokens: number, parts: object[]}|null}
 */
export function estimateSceneWritingCostUsd(modelPricing, size = {}) {
  const listed = sceneWritingCostParts(modelPricing, size);
  if (!listed) return null;
  let typical = 0;
  let worst = 0;
  const parts = [];
  for (const part of listed) {
    const priced = priceScenePart(part);
    if (!priced) return null;
    typical += priced.typical;
    worst += priced.worst;
    parts.push(priced);
  }
  const draft = parts[0];
  return {
    typical,
    worst,
    inputTokens: draft.inputTokens,
    typicalOutput: draft.typicalOutput,
    worstOutput: draft.worstOutput,
    parts,
  };
}

export function formatSceneWritingEstimateLine(band) {
  if (!band || !Number.isFinite(band.typical) || !Number.isFinite(band.worst)) return null;
  const amount = formatEstimateRange(band.typical, band.worst);
  if (!amount) return null;
  return `Estimate: ${formatEstimateAbout(amount)} for this run. ${ESTIMATE_NOT_EXACT}`;
}

/**
 * Dollar line for the Note check that runs on Accept.
 * Same band math as Write draft, for this one call.
 */
export function formatAcceptNoteCheckLine(noteFacts, pricing) {
  const input = Number(noteFacts?.estimated_input_tokens ?? noteFacts?.estimatedInputTokens);
  const typical = Number(noteFacts?.typical_output_tokens ?? noteFacts?.typicalOutput);
  const worst = Number(noteFacts?.worst_output_tokens ?? noteFacts?.worstOutput);
  if (!pricing || ![input, typical, worst].every((value) => Number.isFinite(value) && value >= 0)) {
    return null;
  }
  const band = estimateSceneWritingCostUsd(pricing, {
    estimatedInputTokens: input,
    typicalOutput: typical,
    outputBudget: worst,
  });
  return formatSceneWritingEstimateLine(band);
}

export { formatEstimateUsd, pricingFromCatalog };
